import {
  applyOrderPageSetLayoutSchema,
  createOrderPageSetSchema,
  updateOrderPageSetSchema,
  orderPageSetSchema,
  pageSetSchema,
} from '@messenger/types'
import type {
  ApplicationType,
  GetCreateOrderPageSetNormalized,
  GetOrderPageSetNormalized,
  GetFuneralHomeTemplateNormalized,
  GetGlobalTemplateNormalized,
} from '@messenger/types'
import axios from '@/api/axios.config'
import { z } from 'zod'

export type EnsureOrderPageSetResult = {
  order_product_id: string
  ok: boolean
  created: boolean
  error?: string
  page_set?: GetCreateOrderPageSetNormalized['page_set']
  pages?: GetCreateOrderPageSetNormalized['pages']
}

// One-call page-set bootstrap: send only the order id; the backend loads (or
// creates from each product's default template) every active product's page
// set and returns them all — no per-product template fetch/pick/create chain.
const postEnsureOrderPageSets = (
  orderId: string,
  applicationType: ApplicationType,
) =>
  axios.post<{ results: EnsureOrderPageSetResult[] }>('orderPageSets/ensure', {
    order_id: orderId,
    application_type: applicationType,
  })

// In-flight ensure requests, keyed by order. The customize page's bootstrap
// effect re-runs while its first ensure is still pending (its dependencies
// settle over two renders on mount), and the server has no uniqueness guard:
// every call that finds no page set creates one, so two overlapping calls
// left the product with a blank duplicate page set. Sharing the pending
// request means the second caller gets the first call's result instead of
// triggering a second create.
const ensureInFlight = new Map<
  string,
  ReturnType<typeof postEnsureOrderPageSets>
>()

export const ensureOrderPageSets = (
  orderId: string,
  applicationType: ApplicationType,
) => {
  // MDL and M3 orders live in different tables, so the same id under a
  // different application type is a different order.
  const key = `${applicationType}:${orderId}`
  const pending = ensureInFlight.get(key)
  if (pending) return pending

  // Forget the request once it settles — success or failure — so the next
  // visit to the order (a later render, a retry after an error) asks the
  // server again rather than replaying a stale response.
  const request = postEnsureOrderPageSets(orderId, applicationType).finally(
    () => ensureInFlight.delete(key),
  )
  ensureInFlight.set(key, request)
  return request
}

export const create = async (
  payload: z.infer<typeof createOrderPageSetSchema>,
  applicationType: ApplicationType,
) => {
  return await axios.post<GetCreateOrderPageSetNormalized>(
    `createOrderPageSet`,
    {
      ...payload,
      application_type: applicationType,
    },
  )
}

/** The body Apply layout sends, snake_case as the route reads it. */
export type ApplyOrderPageSetLayoutBody = {
  template_id: string
  template_type: z.infer<typeof applyOrderPageSetLayoutSchema>['templateType']
  pages: Array<{
    grid_start_point_top: number
    grid_start_point_left: number
    rows: number
    columns: number
    page_number: number
    json_template: string
    source_page_id: string
  }>
}

/** Apply layout: the line's page set takes the layout's pages in one
 * request, or keeps everything it had. */
export const applyOrderPageSetLayout = async (
  orderPageSetId: string,
  body: ApplyOrderPageSetLayoutBody,
  applicationType: ApplicationType,
) =>
  axios.post<{
    page_set: GetOrderPageSetNormalized
    pages: Array<Record<string, unknown>>
  }>(`orderPageSets/${orderPageSetId}/layout`, {
    ...body,
    application_type: applicationType,
  })

export const update = async (
  orderPageSetId: string,
  payload: z.infer<typeof updateOrderPageSetSchema>,
  applicationType: ApplicationType,
) => {
  return await axios.patch<GetOrderPageSetNormalized>(
    `orderPageSets/${orderPageSetId}`,
    {
      ...payload,
      application_type: applicationType,
    },
  )
}

// Sibling of updateOrderPage's timeout (see orderPages.ts): save() awaits
// this call before its finally releases the save gates, and the shared axios
// instance has no default timeout — a stall here would otherwise hold
// Save/Back/page-nav hostage until the transport gives up. On expiry the
// rejection lands in updateOrderPageSetByLayoutId's existing catch.
const UPDATE_PAGE_SET_TIMEOUT_MS = 60_000

export const updatePageSetByOrderProductId = async (
  orderProductId: string,
  payload: z.infer<typeof updateOrderPageSetSchema>,
  applicationType: ApplicationType,
) => {
  return await axios.patch<{ count: number }>(
    `orderPageSetsByOrderProductId/${orderProductId}`,
    {
      ...payload,
      application_type: applicationType,
    },
    { timeout: UPDATE_PAGE_SET_TIMEOUT_MS },
  )
}

export const getOrderPageSets = async (
  orderProductId: z.infer<typeof orderPageSetSchema>['orderProductId'],
  applicationType: ApplicationType,
) => {
  return await axios.get<GetOrderPageSetNormalized[]>(
    `orderPageSets?order_product_id=${orderProductId}&application_type=${applicationType}`,
  )
}

export const getOrderPageSetsById = async (orderPageSetId: string) => {
  return await axios.get<GetOrderPageSetNormalized[]>(
    `orderPageSets?id=${orderPageSetId}`,
  )
}

export const getHomeLayoutPageSets = async (
  productId: z.infer<typeof pageSetSchema>['productId'],
  themeId: z.infer<typeof pageSetSchema>['themeId'] = undefined,
  orderId: z.infer<typeof pageSetSchema>['orderId'] = undefined,
  applicationType: z.infer<typeof pageSetSchema>['applicationType'] = 'MDL',
  language: z.infer<typeof pageSetSchema>['language'] = undefined,
) => {
  const queryParams = new URLSearchParams()

  if (productId) {
    queryParams.append('product_id', productId)
  }

  if (themeId) {
    queryParams.append('theme_id', themeId)
  }

  if (orderId) {
    queryParams.append('order_id', orderId)
  }

  queryParams.append('custom_pages', 'true')

  if (applicationType) {
    queryParams.append('application_type', applicationType)
  }

  // Pass the line's effective language so templates filter per line for
  // mixed-language orders. Omitted lets the backend fall back to the order.
  if (language) {
    queryParams.append('language', language)
  }

  return await axios.get<
    GetFuneralHomeTemplateNormalized[] | GetGlobalTemplateNormalized[]
  >(`pageSets?${queryParams.toString()}`)
}
